// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';

// material ui
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import type { GetDeviceLocationDto } from '@/api/generated/recalc/endpoints/v2/devices';

// shared components
import { WritableOnly } from '@/components/access-control';
import LocationMap from '@/components/maps/LocationMap';

// relative
import { formatDeviceLocation } from './devices.utils';

interface DeviceLocationCardProps {
  displayName?: string | null;
  location?: GetDeviceLocationDto | null;
  onEdit?: () => void;
}

const DeviceLocationCard = ({
  displayName,
  location,
  onEdit,
}: DeviceLocationCardProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const hasCoordinates =
    location?.latitude != null && location?.longitude != null;
  const hasLocation = Boolean(formatDeviceLocation(location));

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">
                {t('devices.location', { defaultValue: 'Location' })}
              </Typography>
            </Stack>

            {onEdit ? (
              <WritableOnly>
                <Tooltip title={t('common.editLocation')}>
                  <IconButton onClick={onEdit} size="small">
                    <EditRoundedIcon />
                  </IconButton>
                </Tooltip>
              </WritableOnly>
            ) : null}
          </Stack>

          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {formatDeviceLocation(location) || ''}
          </Typography>

          {hasCoordinates && (
            <LocationMap
              pins={[
                {
                  latitude: location.latitude,
                  longitude: location.longitude,
                  label: displayName || t('devices.location'),
                },
              ]}
              height="32vh"
              zoom={16}
            />
          )}

          {!hasCoordinates && !hasLocation && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RoomOutlinedIcon sx={{ fontSize: 40 }} />
              <Typography variant="body2" color="text.secondary">
                {t('gateways.noLocationSet')}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DeviceLocationCard;
